<template>
	<div>
		<BaseHeader :title="$t('premium_case_referral_overview').toString()" />
		<div class="main" v-if="!isLoading">
			<div :title="$t('open_premium_case_referrals').toString()">
				<div class="mb-4">
					<div class="flex gap-4 justify-end" style="align-items: end">
						<BaseSelect
							ref="monthBaseSelect"
							v-model="selectedMonth"
							:multiple="false"
							:options="monthFilter"
							:filter-only="true"
							class="w-48"
							style="padding-right: 0px!important; padding-bottom: 0px!important;"
							:label="$t('month')"
							@changed="getAllReferrals"
						/>
						<BaseSelect
							ref="yearBaseSelect"
							v-model="selectedYear"
							:multiple="false"
							:options="yearsOptions"
							:filter-only="true"
							class="w-48"
							style="padding-right: 0px!important; padding-bottom: 0px!important;"
							:label="$t('year')"
							@changed="getAllReferrals"
						/>
						<BaseSelect
							ref="queryBaseSelect"
							v-model="selectedFilter"
							:multiple="false"
							:options="queryFilter"
							:filter-only="true"
							class="w-48"
							style="padding-right: 0px!important; padding-bottom: 0px!important;"
							:label="$t('filter')"
							@changed="getAllReferrals"
						/>
					</div>
				</div>
				<div class="flex flex-col">
					<div>
						<div class="m-4" v-if="this.billings && this.billings.length <= 0">
							<b>{{ $t('no_premium_case_referrals') }}</b>
						</div>
						<div
							class="align-middle inline-block min-w-full shadow overflow-hiddenborder-gray-200 rounded"
							v-if="this.billings && this.billings.length > 0"
						>
							<div v-for="billing in this.billings" :key="billing.id">
								<div class="flex flex-row bg-connectProBlue rounded-t text-white p-4" style="text-align: start">
									<div class="w-1/4">Firma: {{ billing.company.name }}</div>
									<div class="w-1/4">Name: {{ billing.company.owner_firstname }} {{ billing.company.owner_lastname }}</div>
									<div class="w-1/4">Mandate (Fremdwerkstatt): {{ billing.meta.count }}</div>
									<div class="w-1/4">
										<font-awesome-icon
											:icon="['fas', 'stamp']"
											class="text-white mr-2"
											v-if="selectedFilter.value === 'not_billed'"
											@click="openSetToBilledModal(billing)"
										/>
									</div>
								</div>
								<div class="flex flex-row p-4 border-b" style="text-align: start" v-for="referral in billing.referrals" :key="referral.id">
									<div class="w-1/4">Vermittelt am: {{ getGermanDate(referral.created_at) }}</div>
									<div class="w-1/4">Aktenzeichen (Kanzlei): {{ referral.damage_case.lawyers_office_reference_number ?? '-' }}</div>
									<div class="w-1/4">
										<div>Werkstatt: {{ referral.damage_case.repair_shop_company?.name ?? '-' }}</div>
										<div>
											Kunde:
											{{
												referral.damage_case.customer?.firstName && referral.damage_case.customer?.lastName
													? referral.damage_case.customer?.firstName + ' ' + referral.damage_case.customer?.lastName
													: '-'
											}}
										</div>
										<div>IMD-NET: {{ referral.damage_case.imd_net ?? '-' }}</div>
									</div>
									<div class="w-1/4">Abgerechnet: {{ referral.is_billed ? 'Ja' : 'Nein' }}</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<LoadingSpinner v-else />
		<router-view />
	</div>
</template>

<script>
import BaseHeader from '@/components/navigation/BaseHeader';
import Authentication from '@/mixins/Authentication';
import DamageAssessorCaseRepository from '@/api/repositories/DamageAssessorCaseRepository';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';
import { getGermanDate, getOptionFromOptionsArray } from '../../plugins/Utils';
import BaseSelect from '@/components/forms/inputs/BaseSelect.vue';
import DocumentFileUploader from '@/components/forms/inputs/DocumentFileUploader.vue';
import PremiumCaseReferralRepository from '@/api/repositories/PremiumCaseReferralRepository';
import BaseButton from '@/components/forms/buttons/BaseButton.vue';

export default {
	name: 'PremiumCaseReferralOverview',
	components: {
		BaseButton,
		DocumentFileUploader,
		BaseSelect,
		LoadingSpinner,
		BaseHeader,
	},
	mixins: [Authentication],
	data() {
		return {
			isLoading: false,
			per_page: { display: '25', value: 25 },
			billings: [],
			queryFilter: [],
			yearFilter: [],
			monthFilter: [],
			selectedFilter: null,
			selectedYear: null,
			selectedMonth: null,
			uploadedCompensationDoc: null,
			isDocument: false,
		};
	},
	mounted() {
		this.yearFilter = [getOptionFromOptionsArray(this.yearsOptions, this.currentYear)];
		this.monthFilter = this.months;
		this.selectedMonth = this.months.find(monthElement => monthElement.value === this.currentMonth);
		this.selectedYear = this.yearFilter.find(yearElement => yearElement.value === this.currentYear);
		this.isLoading = true;
		this.$root.$on('updateCase', () => {
			this.$store.commit('clearCompensation');
			this.getAllReferrals();
		});
		this.getEmployees();
	},
	watch: {
		'$route.name'(newValue, oldValue) {
			if (oldValue === 'setToBilled' && newValue === 'premiumcasereferrals') {
				this.getAllReferrals();
			}
		},
	},
	computed: {
		months() {
			const monthStrings = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
			return monthStrings.map((monthString, index) => {
				return {
					display: monthString,
					value: index + 1,
				};
			});
		},
		currentYear() {
			return new Date().getFullYear();
		},
		currentMonth() {
			return new Date().getMonth() + 1;
		},
		yearsOptions() {
			const list = [];
			const year = this.currentYear;
			for (let i = year; i > year - 10; i--) {
				list.push({
					display: i,
					value: i,
				});
			}
			return list;
		},
	},
	methods: {
		getGermanDate,
		getAllReferrals() {
			new PremiumCaseReferralRepository()
				.getReferrals(this.selectedMonth.value, this.selectedYear.value, this.selectedFilter.value)
				.then(response => {
					this.billings = response.data;
					this.isLoading = false;
				})
				.catch(e => {
					console.log(e);
				});
		},
		getEmployees() {
			new DamageAssessorCaseRepository()
				.getCompanyEmployees(this.$store.getters.getUser.person.company.id)
				.then(response => {
					this.queryFilter = response.data.data.map(element => {
						return {
							value: element.person.personableId,
							display: element.person.firstName + ' ' + element.person.lastName,
						};
					});
					this.queryFilter.unshift({
						value: 'all',
						display: 'Alle',
					});
					this.queryFilter = [
						{
							value: 'not_billed',
							display: 'Offen',
						},
						{
							value: 'billed',
							display: 'Abgeschlossen',
						},
						{
							value: 'all',
							display: 'Alle',
						},
					];
					this.selectedFilter = this.queryFilter[0];
				})
				.then(_ => {
					this.getAllReferrals();
				});
		},
		openSetToBilledModal(billing) {
			// example: emit event to parent
			this.$route.name !== 'setToBilled' &&
				this.$router.push({
					name: 'setToBilled',
					params: { lawyers_office_id: billing.company.lawyers_office_id, referrals: billing.referrals.map(referral => ({ id: referral.id })) },
				});
			// or open SweetAlert / modal here
		},
	},
};
</script>
